import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Heart, X, RotateCcw, SlidersHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import SwipeCard from "../components/swipe/SwipeCard";
import FilterPanel from "../components/swipe/FilterPanel";
import ControlsHelp from "../components/swipe/ControlsHelp";

import { useSwipeEngine } from "@/hooks/useSwipeEngine";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";

const INITIAL_FILTERS = {
    minPrice: 0,
    maxPrice: 500,
    categories: [],
    subcategories: [],
    colors: [],
    brands: [],
    sizes: [],
    inStockOnly: false,
    is_new: false,
};

export default function SwipePage() {
    const swipeCardRef = useRef(null);
    const [showFilters, setShowFilters] = useState(false);
    const [imageViewerState, setImageViewerState] = useState(null);

    const {
        products,
        currentProduct,
        currentIndex,
        totalCount,
        isLoading,
        isReloading,
        filters,
        filterOptions,
        setFilters,
        handleSwipe,
        handleExpandDescription,
        handleVisitSite,
        handleVariantClick,
        handleResetFilters,
    } = useSwipeEngine(INITIAL_FILTERS);

    useKeyboardNavigation({
        swipeCardRef,
        isFiltersOpen: showFilters,
        isImageViewerOpen: !!imageViewerState,
    });

    if (isLoading) {
        return (
            <div className="h-[100dvh] flex flex-col bg-gradient-to-br from-amber-50/40 via-stone-50 to-orange-50/30 dark:from-[hsl(228,14%,6%)] dark:via-[hsl(228,12%,8%)] dark:to-[hsl(225,10%,9%)] overflow-hidden" aria-busy="true" aria-label="Chargement des produits">
                {/* Header skeleton */}
                <div className="p-2 md:p-6 flex items-center justify-between">
                    <div>
                        <div className="skeleton-bone skeleton-stagger-1" style={{ height: '1.5rem', width: '7rem' }} />
                        <div className="skeleton-bone skeleton-stagger-2 mt-1.5" style={{ height: '0.75rem', width: '9rem' }} />
                    </div>
                    <div className="skeleton-bone skeleton-rounded-lg skeleton-filter-btn skeleton-stagger-3" />
                </div>
                {/* Card skeleton */}
                <div className="flex-1 flex items-center justify-center px-1 pb-1 md:px-6">
                    <div className="relative w-full max-w-md h-[calc(100dvh-110px)] md:h-[calc(100vh-140px)]">
                        <div className="skeleton-bone skeleton-rounded-2xl skeleton-stagger-1 w-full h-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (!isReloading && (products.length === 0 || currentIndex >= products.length)) {
        return (
            <div className="h-[100dvh] flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                    <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/40 dark:to-yellow-950/40 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Heart className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                        Plus de produits pour le moment
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Vous avez vu tous les produits disponibles avec vos filtres actuels.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Button
                            onClick={handleResetFilters}
                            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                        >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Réinitialiser les filtres
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[100dvh] flex flex-col bg-gradient-to-br from-amber-50/40 via-stone-50 to-orange-50/30 dark:from-[hsl(228,14%,6%)] dark:via-[hsl(228,12%,8%)] dark:to-[hsl(225,10%,9%)] overscroll-y-none touch-pan-y overflow-hidden relative">
            {/* Subtle filter reload overlay */}
            {isReloading && (
                <div className="absolute inset-0 z-30 bg-white/40 backdrop-blur-[1px] flex items-center justify-center pointer-events-none transition-opacity duration-200">
                    <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
                </div>
            )}
            {/* Header */}
            <div className="p-2 md:p-6 flex items-center justify-between">
                <div>
                    <h1 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">Découvrir</h1>
                    <p className="text-xs text-gray-600">
                        {totalCount - currentIndex > 0 ? totalCount - currentIndex : 0} produits restants
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => setShowFilters(true)}
                    className="gap-2 text-sm h-9 md:h-10 dark:bg-transparent dark:border-amber-500/30 dark:text-gray-200 dark:hover:bg-amber-950/40"
                >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filtres
                </Button>
            </div>

            {/* Swipe Area */}
            <div className="flex-1 flex items-center justify-center px-1 pb-1 md:px-6">
                <div className="relative w-full max-w-md h-[calc(100dvh-110px)] md:h-[calc(100vh-140px)]">
                    {currentProduct && (
                        <SwipeCard
                            ref={swipeCardRef}
                            key={`${currentProduct.id}-${currentIndex}`}
                            product={currentProduct}
                            onSwipe={handleSwipe}
                            onImageClick={(data) => setImageViewerState(data)}
                            allProducts={products}
                            onExpandDescription={handleExpandDescription}
                            onVisitSite={handleVisitSite}
                            onVariantClick={handleVariantClick}
                        />
                    )}
                </div>
            </div>

            {/* Filter Panel Dialog */}
            <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="sm:max-w-md p-0">
                    <FilterPanel
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setShowFilters(false)}
                        products={products}
                        availableOptions={filterOptions}
                    />
                </DialogContent>
            </Dialog>

            {/* Image Detail Dialog - Full Screen Image Only */}
            <Dialog open={!!imageViewerState} onOpenChange={() => setImageViewerState(null)}>
                <DialogContent className="max-w-[95vw] h-[95vh] p-0 overflow-hidden bg-transparent border-0 shadow-none flex items-center justify-center">
                    {imageViewerState && currentProduct && (
                        <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
                            <Button
                                className="absolute top-4 right-4 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white pointer-events-auto"
                                size="icon"
                                onClick={() => setImageViewerState(null)}
                            >
                                <X className="w-6 h-6" />
                            </Button>

                            {/* Navigation Arrows */}
                            {currentProduct.images.length > 1 && (
                                <>
                                    <Button
                                        className="absolute left-4 top-1/2 -translate-y-1/2 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white pointer-events-auto h-12 w-12"
                                        size="icon"
                                        onClick={() => setImageViewerState(prev => ({
                                            ...prev,
                                            index: (prev.index - 1 + currentProduct.images.length) % currentProduct.images.length
                                        }))}
                                    >
                                        <ChevronLeft className="w-8 h-8" />
                                    </Button>
                                    <Button
                                        className="absolute right-4 top-1/2 -translate-y-1/2 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white pointer-events-auto h-12 w-12"
                                        size="icon"
                                        onClick={() => setImageViewerState(prev => ({
                                            ...prev,
                                            index: (prev.index + 1) % currentProduct.images.length
                                        }))}
                                    >
                                        <ChevronRight className="w-8 h-8" />
                                    </Button>
                                </>
                            )}

                            <img
                                src={currentProduct.images[imageViewerState.index]}
                                alt="Full screen view"
                                className="max-w-full max-h-full object-contain pointer-events-auto"
                            />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ControlsHelp />
        </div >
    );
}